import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  type ElementType,
} from "react";
import mapboxgl, { Map as MapboxMap } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { config } from "@/config";
import CircularProgress from "@mui/material/CircularProgress";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { usePage } from "@hooks/usePage";
import Button from "@commons/Button";
import { applyGeoJsonLayer } from "./MapHelpers";
import Text from "@commons/Typography/Text";
import Heading from "@commons/Typography/Heading";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import type {
  DrawEvent,
  GeoJsonFeature,
  GeoJsonFeatureCollection,
} from "@typings/components/map";
import { useNavigate } from "react-router-dom";
import useMapMarkerPopup from "@hooks/useMapMarkerPopup";
import SatelliteAltSharpIcon from "@mui/icons-material/SatelliteAltSharp";

mapboxgl.accessToken = config.mapbox.accessToken;

type MapDrawProps<T> = {
  longitude?: number;
  latitude?: number;
  zoom?: number;
  style?: string;
  geoJsonData?: GeoJsonFeatureCollection<T> | null;
  canCreate?: boolean;
  modalToOpen?: string;
  coordinatesButtonLabel?: string;
  popupComponent?: ElementType<{
    feature: GeoJsonFeature<T>;
    onNavigate: (path: string) => void;
  }>;
};

function MapDraw<T>({
  longitude = config.map?.longitude as number,
  latitude = config.map?.latitude as number,
  zoom = config.map?.zoom as number,
  style = config.map?.style as string,
  popupComponent,
  canCreate = false,
  modalToOpen = "",
  coordinatesButtonLabel = "Novo Dado",
  geoJsonData = null,
}: MapDrawProps<T>) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const { areaMap, openModal, setDataModal } = usePage();
  const navigate = useNavigate();
  const drawRef = useRef<MapboxDraw | null>(null);
  const [newDrawPoint, setNewDrawPoint] = useState<{
    id: string;
    coordinates: [number, number];
  } | null>(null);
  const [isSatellite, setIsSatellite] = useState(false);

  const areaMapRef = useRef(areaMap);

  useEffect(() => {
    areaMapRef.current = areaMap;
  }, [areaMap]);

  useMapMarkerPopup({
    map: mapLoaded ? mapRef.current : null,
    geoJsonData,
    popupComponent,
    navigate,
  });

  const handleDrawPoint = useCallback((event: DrawEvent) => {
    const draw = drawRef.current;
    if (!draw) return;
    if (!event?.features?.length) return;

    const pointsInEvent = event.features.filter(
      (point) => point.geometry?.type === "Point",
    );
    if (pointsInEvent.length === 0) return;

    const lastPoint = pointsInEvent[pointsInEvent.length - 1];

    const allDrawPoints = draw.getAll()?.features ?? [];
    allDrawPoints.forEach((point) => {
      if (point.id !== lastPoint.id && draw.get(point.id as string)) {
        draw.delete(point.id as string);
      }
    });

    setNewDrawPoint({
      id: lastPoint.id,
      coordinates: lastPoint.geometry.coordinates as [number, number],
    });
  }, []);

  function handleCloseDrawPoint() {
    if (newDrawPoint) {
      drawRef.current?.delete(newDrawPoint.id);
      setNewDrawPoint(null);
    }
  }

  const toggleMapStyle = useCallback(() => {
    if (!mapRef.current || !drawRef.current) return;

    const newStyle = isSatellite
      ? config.map?.style
      : config.map?.styleSatellite;

    mapRef.current.removeControl(drawRef.current);
    mapRef.current.setStyle(newStyle || style);
    setIsSatellite(!isSatellite);
  }, [isSatellite, style]);

  useEffect(() => {
    if (!mapRef.current) return;

    const handleStyleData = () => {
      if (!mapRef.current) return;

      if (areaMapRef.current) {
        applyGeoJsonLayer(mapRef.current, areaMapRef.current);
      }

      if (drawRef.current && !mapRef.current.hasControl(drawRef.current)) {
        mapRef.current.addControl(drawRef.current);
      }
    };

    mapRef.current.on("styledata", handleStyleData);

    return () => {
      mapRef.current?.off("styledata", handleStyleData);
    };
  }, [mapLoaded]);

  useEffect(() => {
    if (!mapRef.current || !mapLoaded || !areaMap) return;
    applyGeoJsonLayer(mapRef.current, areaMap);
  }, [mapLoaded, areaMap]);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    setMapLoaded(false);

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style,
      center: [longitude, latitude],
      zoom,
    });

    mapRef.current.on("load", () => {
      if (!mapRef.current) return;
      mapRef.current.setCenter([longitude, latitude]);

      drawRef.current = new MapboxDraw({
        displayControlsDefault: false,
        controls: { point: true },
      });
      mapRef.current.addControl(drawRef.current);
      mapRef.current.on("draw.create", handleDrawPoint);
      mapRef.current.on("draw.update", handleDrawPoint);

      setMapLoaded(true);
    });

    return () => {
      if (mapRef.current && drawRef.current) {
        mapRef.current.off("draw.create", handleDrawPoint);
        mapRef.current.off("draw.update", handleDrawPoint);
        mapRef.current.removeControl(drawRef.current);
      }
      setMapLoaded(false);
      mapRef.current?.remove();
      mapRef.current = null;
      drawRef.current = null;
    };
  }, [longitude, latitude, handleDrawPoint, zoom, style]);

  return (
    <div data-testid="map-container" className="w-full h-full">
      <div ref={mapContainerRef} className="w-full h-full">
        <div className="absolute top-12 right-2 z-10">
          <IconButton
            onClick={toggleMapStyle}
            className="bg-white shadow-lg hover:bg-gray-100"
            aria-label="Alternar visão satélite"
          >
            <SatelliteAltSharpIcon
              className={`${isSatellite ? "text-blue-500" : "text-gray-600"} text-[18px]`}
            />
          </IconButton>
        </div>
        {newDrawPoint && (
          <div className="absolute left-1/2 bottom-12 transform -translate-x-1/2  p-4 bg-white bg-opacity-80 rounded shadow min-w-48 w-54 z-10">
            <div className="flex items-center justify-between mb-2">
              <Heading size="sm">Coordenadas:</Heading>
              <IconButton className="w-4 h-4" onClick={handleCloseDrawPoint}>
                <CloseIcon className="w-4 h-4" />
              </IconButton>
            </div>

            <div className="mb-4">
              <Text className="font-roboto">
                Lat: {newDrawPoint.coordinates[1].toFixed(8)}
              </Text>
              <Text className="font-roboto">
                Lng: {newDrawPoint.coordinates[0].toFixed(8)}
              </Text>
            </div>

            {canCreate && (
              <Button
                customVariant="secondary"
                size="full"
                onClick={() => {
                  setDataModal({
                    latitude: newDrawPoint.coordinates[1].toFixed(8),
                    longitude: newDrawPoint.coordinates[0].toFixed(8),
                  });
                  openModal(modalToOpen);
                  handleCloseDrawPoint();
                }}
                startIcon={<AddIcon />}
              >
                <Text size="sm">{coordinatesButtonLabel}</Text>
              </Button>
            )}
          </div>
        )}
      </div>
      {!mapLoaded && (
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-white bg-opacity-70 z-10">
          <CircularProgress className="text-primary" />
        </div>
      )}
    </div>
  );
}

export default React.memo(MapDraw) as typeof MapDraw;
